'use client';
import { twMerge } from 'tailwind-merge';
import DefaultBannerImageV2 from '@ui/assets/images/DefaultBanner-v2.png';
import { LoadingSkeleton } from './loading-skeleton';
import { IBannerType, Spinner } from '@ui/components';
import BannerOverlayImage from '@ui/assets/images/banner_overlay.png';

const BannerTypeSizeMap = {
  fan: 'h-40 sm:h-[220px] lg:h-[260px] xl:h-[286px] rounded-t-xl',
  creator: 'h-20 rounded-t-md',
  preview: 'rounded-t-xl max-h-[160px]',
  content: 'h-40 rounded-t-md',
};

interface IBanner {
  image?: string;
  type: IBannerType;
  className?: string;
  isLoading?: boolean;
  showSpinner?: boolean;
  showOverlay?: boolean;
  onImageLoad?: () => void;
}

export function BannerV2({
  image,
  type,
  isLoading,
  className,
  showSpinner = false,
  showOverlay = false,
  onImageLoad,
}: IBanner) {
  const typeStyles = BannerTypeSizeMap[type];
  const bgImage = image || DefaultBannerImageV2.src;
  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const imageElement = event.currentTarget;
    if (typeof imageElement.decode !== 'function') {
      onImageLoad?.();
      return;
    }

    void imageElement
      .decode()
      .catch(() => undefined)
      .then(() => onImageLoad?.());
  };

  return isLoading ? (
    <LoadingSkeleton className={twMerge('w-full banner-v2-responsive', typeStyles, className)} />
  ) : (
    <div className="relative">
      {showOverlay && (
        <img
          className={twMerge(
            'h-full w-full absolute object-cover banner-v2-responsive',
            typeStyles,
            className,
          )}
          src={BannerOverlayImage.src}
          style={{ zIndex: 9 }}
        />
      )}

      {showSpinner && (
        <Spinner className="absolute z-10 animate-spin top-44 left-32" fill={'#FF600D'} />
      )}

      <img
        className={twMerge('w-full object-cover banner-v2-responsive', typeStyles, className)}
        src={bgImage}
        onLoad={handleImageLoad}
        onError={(event) => {
          if (event.currentTarget.dataset.fallbackApplied !== 'true') {
            event.currentTarget.dataset.fallbackApplied = 'true';
            event.currentTarget.src = DefaultBannerImageV2.src;
          }
        }}
        alt=""
        decoding="async"
      />
    </div>
  );
}
